import type { CmsCategory } from '@/types/cms/category'

type StrapiImage = {
    data?: {
        attributes?: {
            url?: string
        }
    }
}

type StrapiCategory = {
    id: number
    attributes?: {
        name?: string
        order?: number
        slug?: string
        status?: string
        image?: StrapiImage
    }
}

type StrapiResponse<T> = {
    data: T[]
}

const CMS_BASE_URL = import.meta.env.VITE_STRAPI_RS || ''
const CATEGORY_ENDPOINT = '/api/product-categories'

const buildImageUrl = (path?: string | null) => {
    if (!path) return null
    if (path.startsWith('http')) return path
    if (!CMS_BASE_URL) return path
    return `${CMS_BASE_URL}${path}`
}

const buildCategoriesUrl = () => {
    const url = new URL(CATEGORY_ENDPOINT, CMS_BASE_URL)
    const params = new URLSearchParams()

    params.append('fields[0]', 'name')
    params.append('fields[1]', 'order')
    params.append('fields[2]', 'slug')
    params.append('fields[3]', 'status')
    params.append('populate[image][fields][0]', 'url')

    url.search = params.toString()
    return url.toString()
}

const mapCategory = (category: StrapiCategory): CmsCategory => {
    const name = category.attributes?.name ?? ''
    const slug = category.attributes?.slug ?? ''
    const order = category.attributes?.order ?? 0
    const status = category.attributes?.status ?? undefined
    const imageUrl = buildImageUrl(
        category.attributes?.image?.data?.attributes?.url
    )

    return {
        id: category.id,
        name,
        slug,
        order,
        status: status === 'inactive' ? 'inactive' : 'active',
        imageUrl
    }
}

const getCategories = async (): Promise<CmsCategory[]> => {
    if (!CMS_BASE_URL) {
        throw new Error('CMS base URL is not configured')
    }
    const response = await fetch(buildCategoriesUrl())
    if (!response.ok) {
        throw new Error('Failed to fetch categories')
    }
    const data = (await response.json()) as StrapiResponse<StrapiCategory>
    return data.data.map(mapCategory)
}

export const cmsCategoryService = {
    getCategories
}
